import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { reconcileAppointmentFinancial } from "@/lib/services/appointment-financial-reconciliation";
import type {
  AppointmentFinancialRow,
  AppointmentFinancialSource,
  AppointmentFinancialStatus,
} from "@/lib/utils/appointment-financial";
import i18n from "@/i18n";

interface UpsertAppointmentFinancialInput {
  appointmentId: string;
  status?: AppointmentFinancialStatus;
  source?: AppointmentFinancialSource;
  ownerPackageId?: string | null;
  saleId?: string | null;
  isCoveredByPackage?: boolean;
}

export const useAppointmentFinancial = (appointmentId?: string | null) => {
  return useQuery({
    queryKey: ["appointment-financial", appointmentId],
    queryFn: async (): Promise<AppointmentFinancialRow | null> => {
      if (!appointmentId) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_financials")
        .select("*")
        .eq("appointment_id", appointmentId)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as AppointmentFinancialRow | null;
    },
    enabled: Boolean(appointmentId),
  });
};

export const useUpsertAppointmentFinancial = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const invalidateFinancialQueries = (appointmentId: string) => {
    queryClient.invalidateQueries({ queryKey: ["appointment-financial", appointmentId] });
    queryClient.invalidateQueries({ queryKey: ["appointments"] });
    queryClient.invalidateQueries({ queryKey: ["appointment", appointmentId] });
    queryClient.invalidateQueries({ queryKey: ["sale-by-appointment", appointmentId] });
    queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
    queryClient.invalidateQueries({ queryKey: ["reports-overview"] });
    queryClient.invalidateQueries({ queryKey: ["reports-by-professional"] });
    queryClient.invalidateQueries({ queryKey: ["reports-comparatives"] });
    queryClient.invalidateQueries({ queryKey: ["finalized-appointments"] });
  };

  return useMutation({
    mutationFn: async (input: UpsertAppointmentFinancialInput) => {
      const payload = {
        appointment_id: input.appointmentId,
        status: input.status ?? "pendente",
        source: input.source ?? "manual",
        owner_package_id: input.ownerPackageId ?? null,
        sale_id: input.saleId ?? null,
        is_covered_by_package: input.isCoveredByPackage ?? false,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_financials")
        .upsert(payload, { onConflict: "appointment_id" })
        .select()
        .single();

      if (error) throw error;
      return data as AppointmentFinancialRow;
    },
    onSuccess: (result) => {
      invalidateFinancialQueries(result.appointment_id);
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("calendar:phase8.appointmentFinancial.updateErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useReconcileAppointmentFinancial = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidateFinancialQueries = (appointmentId: string) => {
    queryClient.invalidateQueries({ queryKey: ["appointment-financial", appointmentId] });
    queryClient.invalidateQueries({ queryKey: ["appointments"] });
    queryClient.invalidateQueries({ queryKey: ["appointment", appointmentId] });
    queryClient.invalidateQueries({ queryKey: ["sale-by-appointment", appointmentId] });
    queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
    queryClient.invalidateQueries({ queryKey: ["reports-overview"] });
    queryClient.invalidateQueries({ queryKey: ["reports-by-professional"] });
    queryClient.invalidateQueries({ queryKey: ["reports-comparatives"] });
    queryClient.invalidateQueries({ queryKey: ["finalized-appointments"] });
  };

  return useMutation({
    mutationFn: async (appointmentId: string) => reconcileAppointmentFinancial(appointmentId),
    onSuccess: (result) => {
      invalidateFinancialQueries(result.appointment_id);
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("calendar:phase8.appointmentFinancial.updateErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
